/**
 * Date utility functions for vacation management
 * Handles business day calculations, Brazilian date formatting, and CLT validation
 */

import { format, differenceInCalendarDays, addDays, isWeekend, parseISO, differenceInBusinessDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'

/**
 * Calculate business days between two dates (excludes weekends)
 * @param startDate - Start date
 * @param endDate - End date (inclusive)
 * @returns Number of business days
 */
export function calculateBusinessDays(startDate: Date, endDate: Date): number {
  let days = 0
  const current = new Date(startDate)
  const end = new Date(endDate)

  // Ensure we're working with date-only values
  current.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)

  while (current <= end) {
    if (!isWeekend(current)) {
      days++
    }
    current.setDate(current.getDate() + 1)
  }

  return days
}

/**
 * Format date for Brazilian locale (dd/MM/yyyy)
 * @param date - Date string (ISO 8601) or Date object
 * @returns Formatted date string
 */
export function formatDateBR(date: string | Date): string {
  if (!date) return '--/--/----'

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return '--/--/----'
    }

    return format(dateObj, 'dd/MM/yyyy', { locale: ptBR })
  } catch (error) {
    console.error('Invalid date format:', date, error)
    return '--/--/----'
  }
}

/**
 * Format date range for display
 * @param startDate - Start date (ISO 8601)
 * @param endDate - End date (ISO 8601)
 * @returns Formatted date range string
 */
export function formatDateRange(startDate: string, endDate: string): string {
  return `${formatDateBR(startDate)} - ${formatDateBR(endDate)}`
}

/**
 * Check if date is at least 14 days in future (CLT requirement)
 * @param date - Date to check
 * @returns True if meets advance notice requirement
 */
export function meetsAdvanceNotice(date: Date): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const targetDate = new Date(date)
  targetDate.setHours(0, 0, 0, 0)

  const diffDays = differenceInCalendarDays(targetDate, today)
  return diffDays >= 14
}

/**
 * Convert Date to ISO 8601 date string (YYYY-MM-DD)
 * @param date - Date object
 * @returns ISO date string
 */
export function toISODateString(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

/**
 * Parse ISO date string to Date object (midnight local time)
 * @param dateStr - ISO date string (YYYY-MM-DD)
 * @returns Date object
 */
export function parseISODateString(dateStr: string): Date {
  const date = parseISO(dateStr)
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * Check if a date should be disabled in date picker
 * @param date - Date to check
 * @returns True if date should be disabled
 */
export function shouldDisableDate(date: Date): boolean {
  // Disable weekends
  if (isWeekend(date)) {
    return true
  }

  // Disable past dates
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  if (date < today) {
    return true
  }

  // Disable dates less than 14 days from today
  if (!meetsAdvanceNotice(date)) {
    return true
  }

  return false
}

/**
 * Get the minimum selectable date (14 days from today, not a weekend)
 * @returns Minimum date
 */
export function getMinimumSelectableDate(): Date {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  let minDate = addDays(today, 14)

  // If minimum date falls on weekend, move to next Monday
  while (isWeekend(minDate)) {
    minDate = addDays(minDate, 1)
  }

  return minDate
}

/**
 * Format date for display with day of week (e.g., "Seg, 15/01/2025")
 * @param date - Date string (ISO 8601) or Date object
 * @returns Formatted date string with day name
 */
export function formatDateWithDay(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return format(dateObj, "EEE, dd/MM/yyyy", { locale: ptBR })
}
